import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { apiClient } from "@/services/api/client";
import { sendWelcomeEmail } from "@/lib/send-welcome-email";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';

const HOW_HEARD_OPTIONS = [
  { key: 'facebook',         en: 'Facebook',         fr: 'Facebook' },
  { key: 'linkedin',         en: 'LinkedIn',          fr: 'LinkedIn' },
  { key: 'instagram',        en: 'Instagram',         fr: 'Instagram' },
  { key: 'networking_group', en: 'Networking Group',  fr: 'Groupe de réseautage' },
  { key: 'youtube',          en: 'YouTube',           fr: 'YouTube' },
  { key: 'google_search',    en: 'Google Search',     fr: 'Recherche Google' },
  { key: 'friends',          en: 'Friends',           fr: 'Amis' },
  { key: 'family',           en: 'Family',            fr: 'Famille' },
  { key: 'other',            en: 'Other',             fr: 'Autre' },
];

const TOTAL_STEPS = 4;

interface FormState {
  // Step 1 - Account
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  country: string;
  city: string;
  // Step 2 - Profile
  companyName: string;
  professionalTitle: string;
  yearsExperience: string;
  // Step 3 - Sourcing strategy
  targetIndustries: string;
  expectedDealVolume: string;
  howHeard: string;
  // Step 4 - Tax & legal
  taxFormType: "W-9" | "W-8BEN" | "T4A" | "";
  taxId: string;
  legalName: string;
  signatureName: string;
  signatureDate: string;
  referralAgreementAccepted: boolean;
  legalConsent: boolean;
}

const initial: FormState = {
  firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "",
  country: "", city: "",
  companyName: "", professionalTitle: "", yearsExperience: "",
  targetIndustries: "", expectedDealVolume: "", howHeard: "",
  taxFormType: "", taxId: "", legalName: "", signatureName: "", signatureDate: new Date().toISOString().slice(0, 10),
  referralAgreementAccepted: false, legalConsent: false,
};



export default function RegisterDealSourcer() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const lang = (language as "en" | "fr") || "en";

  const [step, setStep] = useState(1);
  const [data, setData] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailInUse, setEmailInUse] = useState(false);

  const update = (u: Partial<FormState>) => setData((p) => ({ ...p, ...u }));

  const t = (en: string, fr: string) => (lang === "fr" ? fr : en);

  const validate = (s: number) => {
    const e: Record<string, string> = {};
    if (s === 1) {
      if (!data.firstName) e.firstName = t("Required", "Requis");
      if (!data.lastName) e.lastName = t("Required", "Requis");
      if (!data.email) e.email = t("Required", "Requis");
      if (data.password.length < 8) e.password = t("Min 8 chars", "8 caractères min");
      if (data.password !== data.confirmPassword) e.confirmPassword = t("Passwords don't match", "Mots de passe différents");
      if (!data.country) e.country = t("Required", "Requis");
    }
    if (s === 4) {
      if (!data.taxFormType) e.taxFormType = t("Select tax form", "Sélectionnez le formulaire fiscal");
      if (!data.taxId) e.taxId = t("Tax ID is required", "ID fiscal requis");
      if (!data.legalName) e.legalName = t("Legal name is required", "Nom légal requis");
      if (!data.signatureName) e.signatureName = t("Signature required", "Signature requise");
      if (!data.referralAgreementAccepted) e.referralAgreementAccepted = t("Accept the agreement", "Acceptez l'accord");
      if (!data.legalConsent) e.legalConsent = t("Consent required", "Consentement requis");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step) && step < TOTAL_STEPS) { setStep(step + 1); window.scrollTo(0, 0); } };
  const back = () => { if (step > 1) { setStep(step - 1); window.scrollTo(0, 0); } };

  const submit = async () => {
    if (!validate(4)) return;
    setSubmitting(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const user = cred.user;
      await signInWithEmailAndPassword(auth, data.email, data.password);
      await updateProfile(user, { displayName: `${data.firstName} ${data.lastName}` });

      await setDoc(doc(db, "users", user.uid), {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: `${data.firstName} ${data.lastName}`,
        phone: data.phone,
        country: data.country,
        city: data.city,
        companyName: data.companyName,
        roles: ["deal_sourcer"],
        status: "pending_review",
        language: lang,
        dealSourcerProfile: {
          yearsExperience: data.yearsExperience,
          professionalTitle: data.professionalTitle,
          targetIndustries: data.targetIndustries,
          expectedDealVolume: data.expectedDealVolume,
          howHeard: data.howHeard,
        },
        taxInfo: {
          formType: data.taxFormType,
          taxId: data.taxId,
          legalName: data.legalName || `${data.firstName} ${data.lastName}`,
        },
        referralAgreement: {
          accepted: true,
          signatureName: data.signatureName,
          signatureDate: data.signatureDate,
          signedAt: new Date().toISOString(),
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      try {
        const idToken = await user.getIdToken(true);
        await apiClient.post(
          "/auth/assign-role",
          { uid: user.uid, role: "deal_sourcer" },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        await user.getIdToken(true);
      } catch (err) {
        console.warn("[RegisterDealSourcer] assign-role failed (non-blocking):", err);
      }

      sendWelcomeEmail({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: "deal_sourcer" as any,
        language: lang,
      }).catch((e) => console.warn("[RegisterDealSourcer] welcome email failed:", e));

      toast({ title: t("Registration successful!", "Inscription réussie !") });
      navigate("/dashboard/deal-sourcer");
    } catch (err: any) {
      console.error(err);
      if (err?.code === "auth/email-already-in-use") {
        // Tenter de se connecter et ajouter le rôle
        try {
          const existingCred = await signInWithEmailAndPassword(auth, data.email, data.password);
          const user = existingCred.user;
          
          // Ajouter le rôle deal_sourcer au compte existant
          await setDoc(doc(db, "users", user.uid), {
            roles: ["deal_sourcer"],
            dealSourcerProfile: {
              yearsExperience: data.yearsExperience,
              professionalTitle: data.professionalTitle,
              targetIndustries: data.targetIndustries,
              expectedDealVolume: data.expectedDealVolume,
              howHeard: data.howHeard,
            },
            taxInfo: {
              formType: data.taxFormType,
              taxId: data.taxId,
              legalName: data.legalName || `${data.firstName} ${data.lastName}`,
            },
            referralAgreement: {
              accepted: true,
              signatureName: data.signatureName,
              signatureDate: data.signatureDate,
              signedAt: new Date().toISOString(),
            },
            updatedAt: serverTimestamp(),
          }, { merge: true });

          toast({ title: t("Role added successfully!", "Rôle ajouté avec succès!") });
          navigate("/dashboard/deal-sourcer");
        } catch (loginErr: any) {
          setEmailInUse(true);
          toast({
            title: t("Error", "Erreur"),
            description: t(
              "This email is already registered. Please sign in first.",
              "Cet email est déjà enregistré. Veuillez vous connecter d'abord."
            ),
            variant: "destructive",
          });
        }
        toast({
          title: t("Error", "Erreur"),
          description: t("This email is already registered.", "Cet email est déjà enregistré."),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("Error", "Erreur"),
          description: err?.message || t("Something went wrong", "Une erreur est survenue"),
          variant: "destructive",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      <div className="flex justify-center mb-6">
        <a href="/"><img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" /></a>
      </div>

      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="w-32" />
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              {t("Deal Sourcer Registration", "Inscription Deal Sourcer")}
            </h1>
            <div className="w-32 flex justify-end"><LanguageSelector /></div>
          </div>
          <p className="text-white/70 text-center">
            {t("Step", "Étape")} {step} {t("of", "sur")} {TOTAL_STEPS}
          </p>
          <div className="mt-6 flex gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-2 flex-1 rounded-full ${i + 1 <= step ? "bg-[#CEAB65]" : "bg-white/20"}`}
              />
            ))}
          </div>
        </div>

        {emailInUse && (
          <div className="bg-red-50 border border-red-200 rounded-b-xl p-4 text-center">
            <p className="text-red-700 text-sm mb-2">
              {t(
                "This email is already registered. Would you like to sign in instead?",
                "Cet email est déjà enregistré. Voulez-vous vous connecter ?"
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/login")}
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              {t("Sign In", "Se connecter")}
            </Button>
          </div>
        )}

        <Card className="rounded-t-none rounded-b-xl">
          <CardContent className="p-6 md:p-8 space-y-4">
            {step === 1 && (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>{t("First name", "Prénom")}*</Label>
                  <Input value={data.firstName} onChange={(e) => update({ firstName: e.target.value })} />
                  {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                </div>
                <div>
                  <Label>{t("Last name", "Nom")}*</Label>
                  <Input value={data.lastName} onChange={(e) => update({ lastName: e.target.value })} />
                  {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                </div>
                <div className="md:col-span-2">
                  <Label>{t("Email", "Courriel")}*</Label>
                  <Input type="email" value={data.email} onChange={(e) => update({ email: e.target.value })} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <Label>{t("Phone", "Téléphone")}</Label>
                  <Input value={data.phone} onChange={(e) => update({ phone: e.target.value })} />
                </div>
                <div>
                  <Label>{t("Country", "Pays")}*</Label>
                  <Select value={data.country} onValueChange={(v) => update({ country: v })}>
                    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">{t("United States", "États-Unis")}</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="MX">{t("Mexico", "Mexique")}</SelectItem>
                      <SelectItem value="OTHER">{t("Other", "Autre")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.country && <p className="text-xs text-destructive">{errors.country}</p>}
                </div>
                <div>
                  <Label>{t("City", "Ville")}</Label>
                  <Input value={data.city} onChange={(e) => update({ city: e.target.value })} />
                </div>
                <div>
                  <Label>{t("Password", "Mot de passe")}*</Label>
                  <Input type="password" value={data.password} onChange={(e) => update({ password: e.target.value })} />
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>
                <div>
                  <Label>{t("Confirm password", "Confirmer")}*</Label>
                  <Input type="password" value={data.confirmPassword} onChange={(e) => update({ confirmPassword: e.target.value })} />
                  {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-4">
                <div>
                  <Label>{t("Company name (optional)", "Nom de l'entreprise (optionnel)")}</Label>
                  <Input value={data.companyName} onChange={(e) => update({ companyName: e.target.value })} />
                </div>
                <div>
                  <Label>{t("Professional title", "Titre professionnel")}</Label>
                  <Select value={data.professionalTitle} onValueChange={(v) => update({ professionalTitle: v })}>
                    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CPA">CPA</SelectItem>
                      <SelectItem value="Lawyer">{t("Lawyer", "Avocat")}</SelectItem>
                      <SelectItem value="Notary">{t("Notary", "Notaire")}</SelectItem>
                      <SelectItem value="Wealth Manager">{t("Wealth Manager", "Gestionnaire de patrimoine")}</SelectItem>
                      <SelectItem value="Business Owner">{t("Business Owner", "Propriétaire d'entreprise")}</SelectItem>
                      <SelectItem value="Other">{t("Other", "Autre")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("Years of professional experience", "Années d'expérience professionnelle")}</Label>
                  <Select value={data.yearsExperience} onValueChange={(v) => update({ yearsExperience: v })}>
                    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-2">0-2</SelectItem>
                      <SelectItem value="3-5">3-5</SelectItem>
                      <SelectItem value="6-10">6-10</SelectItem>
                      <SelectItem value="10+">10+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-4">
                <div>
                  <Label>{t("Target industries", "Industries ciblées")}</Label>
                  <Textarea rows={3} value={data.targetIndustries} onChange={(e) => update({ targetIndustries: e.target.value })} />
                </div>
               <div>
  <Label>{t("How did you hear about us?", "Comment nous avez-vous connu ?")}</Label>
  <Select value={data.howHeard} onValueChange={(v) => update({ howHeard: v })}>
    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
    <SelectContent>
      {HOW_HEARD_OPTIONS.map(opt => (
        <SelectItem key={opt.key} value={opt.key}>
          {lang === 'fr' ? opt.fr : opt.en}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
</div>
                <div>
                  <Label>{t("How did you hear about us?", "Comment nous avez-vous connu ?")}</Label>
                  <Select value={data.howHeard} onValueChange={(v) => update({ howHeard: v })}>
                    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                      <SelectItem value="Referral from a colleague">{t("Referral from a colleague", "Recommandation d'un collègue")}</SelectItem>
                      <SelectItem value="Google search">{t("Google search", "Recherche Google")}</SelectItem>
                      <SelectItem value="Social media">{t("Social media (Facebook, Instagram)", "Réseaux sociaux (Facebook, Instagram)")}</SelectItem>
                      <SelectItem value="Networking event">{t("Networking event", "Événement de réseautage")}</SelectItem>
                      <SelectItem value="Email campaign">{t("Email campaign", "Campagne par courriel")}</SelectItem>
                      <SelectItem value="Other">{t("Other", "Autre")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D314D]">
                    {t("Tax form", "Formulaire fiscal")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "Required to pay your commissions. Choose based on your country.",
                      "Requis pour le paiement de vos commissions. Choisissez selon votre pays."
                    )}
                  </p>
                </div>

                <div>
                  <Label>{t("Tax form type", "Type de formulaire")}*</Label>
                  <Select value={data.taxFormType} onValueChange={(v) => update({ taxFormType: v as any })}>
                    <SelectTrigger><SelectValue placeholder={t("Select", "Sélectionner")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="W-9">W-9 ({t("US persons", "Personnes US")})</SelectItem>
                      <SelectItem value="W-8BEN">W-8BEN ({t("Non-US individuals", "Non-US")})</SelectItem>
                      <SelectItem value="T4A">T4A ({t("Canadian residents", "Résidents canadiens")})</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.taxFormType && <p className="text-xs text-destructive">{errors.taxFormType}</p>}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{t("Tax ID / SSN / SIN", "ID fiscal / SSN / NAS")}*</Label>
                    <Input value={data.taxId} onChange={(e) => update({ taxId: e.target.value })} />
                    {errors.taxId && <p className="text-xs text-destructive">{errors.taxId}</p>}
                  </div>
                  <div>
                    <Label>{t("Legal name on tax form", "Nom légal sur le formulaire")}*</Label>
                    <Input value={data.legalName} onChange={(e) => update({ legalName: e.target.value })} placeholder={`${data.firstName} ${data.lastName}`} />
                    {errors.legalName && <p className="text-xs text-destructive">{errors.legalName}</p>}
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
                  <h4 className="font-semibold text-[#1D314D]">
                    {t("Referral Agreement", "Accord de référence")}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t(
                      "By signing, you agree to act as an independent Deal Sourcer and receive commissions based on the published schedule ($250 to $3,500 per closed deal). Commissions are paid 30 days after closing. You confirm you act in your own name and are responsible for declaring this income.",
                      "En signant, vous acceptez d'agir comme Deal Sourcer indépendant et de recevoir des commissions selon le barème publié (250$ à 3 500$ par deal clôturé). Les commissions sont payées 30 jours après le closing. Vous confirmez agir en votre nom et êtes responsable de déclarer ce revenu."
                    )}
                  </p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>{t("Electronic signature (full name)", "Signature électronique (nom complet)")}*</Label>
                      <Input value={data.signatureName} onChange={(e) => update({ signatureName: e.target.value })} />
                      {errors.signatureName && <p className="text-xs text-destructive">{errors.signatureName}</p>}
                    </div>
                    <div>
                      <Label>{t("Date", "Date")}</Label>
                      <Input type="date" value={data.signatureDate} onChange={(e) => update({ signatureDate: e.target.value })} />
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={data.referralAgreementAccepted}
                      onCheckedChange={(c) => update({ referralAgreementAccepted: !!c })}
                    />
                    <span>{t("I accept the Referral Agreement above.", "J'accepte l'Accord de référence ci-dessus.")}</span>
                  </label>
                  {errors.referralAgreementAccepted && <p className="text-xs text-destructive">{errors.referralAgreementAccepted}</p>}
                </div>

                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={data.legalConsent}
                    onCheckedChange={(c) => update({ legalConsent: !!c })}
                  />
                  <span>
                    {t(
                      "I consent to the processing of my data and confirm the tax information provided is accurate.",
                      "Je consens au traitement de mes données et confirme l'exactitude des informations fiscales fournies."
                    )}
                  </span>
                </label>
                {errors.legalConsent && <p className="text-xs text-destructive">{errors.legalConsent}</p>}
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={back} disabled={step === 1}>
                {t("Back", "Précédent")}
              </Button>
              {step < TOTAL_STEPS ? (
                <Button onClick={next} className="bg-[#1D314D] hover:bg-[#1D314D]/90 text-white">
                  {t("Next", "Suivant")}
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="bg-[#CEAB65] hover:bg-[#CEAB65]/90 text-[#1D314D] font-semibold"
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("Finalize registration", "Finaliser l'inscription")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
